import { useContext, useMemo } from 'react'
import moment from 'moment'
import { useIntl } from 'react-intl'
import { formatMiddleDotSeparator } from 'LuxKit/intl/separators'
import Image from 'components/Common/Image'
import { INTL_DAY_MONTH_NAME, INTL_SHORT_TIME_FORMAT_AM_PM } from 'constants/dateFormats'
import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import { nonNullable } from 'lib/array/arrayUtils'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { useExperience } from 'tripPlanner/hooks/api/offer'
import AddedBookingTile, { AddedBookingTileImageFrame } from './AddedBookingTile'

interface Props {
  experienceId: string
  /** Missing from older or untitled bookings; the fetched experience's name stands in. */
  propertyName?: string
  checkInDate?: string
  time?: string
}

export default function AddedExperienceBookingTile({
  experienceId,
  propertyName,
  checkInDate,
  time,
}: Props) {
  const intl = useIntl()
  const currentCurrency = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)
  const formatDate = useIntlDateFormatter()
  const { data: experience } = useExperience({
    experienceId,
    currentCurrency,
    currentRegionCode,
  })

  const subtitle = nonNullable([
    checkInDate ? formatDate(checkInDate, INTL_DAY_MONTH_NAME) : undefined,
    time ? formatDate(moment(time, 'HH:mm'), INTL_SHORT_TIME_FORMAT_AM_PM) : undefined,
  ]).join(formatMiddleDotSeparator(intl))

  const title = propertyName ?? experience?.name
  const thumbnailImage = experience?.images[0]
  const thumbnail = useMemo(() => thumbnailImage && (
    <AddedBookingTileImageFrame>
      <Image image={thumbnailImage} alt={title} fit="center" objectFit="cover" />
    </AddedBookingTileImageFrame>
  ), [thumbnailImage, title])

  return <AddedBookingTile title={title} subtitle={subtitle} thumbnail={thumbnail} />
}
